import React, { useCallback } from 'react';
import {
  ReactFlow,
  addEdge,
  Background,
  useNodesState,
  useEdgesState,
  MiniMap,
  Controls,
  Node,
  Edge,
} from '@xyflow/react';
import { useModalControl, Modal, IconButton, PreviewIcon } from '@weave/design-system';
import dagre from 'dagre';
import '@xyflow/react/dist/style.css';
import { REACT_APP_API_URL } from 'config/app';
import { useQuery } from 'react-query';

import {
  API as OrgHierarchyViewerService,
  GetOrgHierarchyResponse,
  OrgHierarchy as ApiOrgHierarchy,
} from '@weave/schema-gen-ts/dist/schemas/organization/hierarchy-viewer/v1/api.pb';
import { authorizedFetch } from '../../../helpers/utils';

const isDev = (REACT_APP_API_URL as string).includes('dev');

const NODE_COLORS = {
  organization: {
    backgroundColor: '#8E44AD',
    borderColor: '#6C3483',
  },
  division: {
    backgroundColor: '#3498DB',
    borderColor: '#2471A3',
  },
  location: {
    backgroundColor: '#1ABC9C',
    borderColor: '#148F77',
  },
  tenantGroupPDS: {
    backgroundColor: 'rgba(255, 165, 0, 0.1)',
    borderColor: '#FF8C00',
    labelColor: '#FF8C00',
  },
  tenantGroupLisa: {
    backgroundColor: 'rgba(147, 51, 234, 0.1)',
    borderColor: '#9333EA',
    labelColor: '#9333EA',
  },
};

const BackgroundRect = ({ data }: any) => {
  const [isHovered, setIsHovered] = React.useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (data.tenantId) {
      // Construct URL based on environment
      const baseUrl = isDev ? 'https://pds.weavedev.net' : 'https://pds.weavelab.ninja';
      const url = `${baseUrl}/tenants/${data.tenantId}`;
      window.open(url, '_blank');
    }
  };

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        cursor: data.tenantId ? 'pointer' : 'default',
        backgroundColor: isHovered
          ? data.backgroundColor.replace('0.1)', '0.2)')
          : data.backgroundColor,
        border: data.border,
        borderRadius: '8px',
        transition: 'background-color 0.2s ease, opacity 0.2s ease',
      }}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        style={{
          position: 'absolute',
          left: '8px',
          fontSize: data.fontSize || '14px',
          fontWeight: 'bold',
          color: data.labelColor,
          pointerEvents: 'none',
        }}
      >
        {data.label}
      </div>
    </div>
  );
};

const nodeTypes = {
  backgroundRect: BackgroundRect,
};

export const LocationHierarchy = ({ orgId }: { orgId: string }) => {
  const { modalProps, triggerProps } = useModalControl();

  return (
    <>
      <IconButton label="View Hierarchy" showLabelOnHover={true} {...triggerProps}>
        <PreviewIcon />
      </IconButton>
      <Modal {...modalProps} maxWidth={1000} css={{ maxHeight: 1000, height: '100%' }}>
        <Modal.Header>Location Hierarchy</Modal.Header>
        <Modal.Body>
          <DataLayer orgId={orgId} render={(data) => <NestedFlow data={data} />} />
        </Modal.Body>
      </Modal>
    </>
  );
};

const DataLayer = ({
  orgId,
  render,
}: {
  orgId: string;
  render: (data: ApiOrgHierarchy) => React.ReactNode;
}) => {
  const { data } = useQuery({
    queryKey: ['orgHierarchy', orgId],
    queryFn: async () => {
      return OrgHierarchyViewerService.GetOrgHierarchy(
        authorizedFetch<GetOrgHierarchyResponse>(),
        {
          orgId,
        }
      );
    },
  });

  if (!data?.hierarchy) {
    return <div>Loading...</div>;
  }

  return <>{render(data.hierarchy)}</>;
};

const LegendItem = ({
  backgroundColor,
  border,
  label,
}: {
  backgroundColor: string;
  border: string;
  label: string;
}) => (
  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
    <div
      style={{
        width: '20px',
        height: '20px',
        backgroundColor,
        border,
        borderRadius: '4px',
        marginRight: '8px',
      }}
    />
    <span>{label}</span>
  </div>
);

const Legend = () => {
  return (
    <div
      style={{
        position: 'absolute',
        top: '20px',
        right: '20px',
        backgroundColor: 'white',
        padding: '12px',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        zIndex: 5,
        fontSize: '12px',
      }}
    >
      <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Legend</div>
      <LegendItem
        backgroundColor={NODE_COLORS.organization.backgroundColor}
        border={`2px solid ${NODE_COLORS.organization.borderColor}`}
        label="Organization"
      />
      <LegendItem
        backgroundColor={NODE_COLORS.division.backgroundColor}
        border={`2px solid ${NODE_COLORS.division.borderColor}`}
        label="Division"
      />
      <LegendItem
        backgroundColor={NODE_COLORS.location.backgroundColor}
        border={`2px solid ${NODE_COLORS.location.borderColor}`}
        label="Location"
      />
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 0 }}>
        <div style={{ display: 'flex', gap: '4px', marginRight: '8px' }}>
          <div
            style={{
              width: '20px',
              height: '20px',
              backgroundColor: NODE_COLORS.tenantGroupPDS.backgroundColor,
              border: `2px dashed ${NODE_COLORS.tenantGroupPDS.borderColor}`,
              borderRadius: '4px',
            }}
          />
          <div
            style={{
              width: '20px',
              height: '20px',
              backgroundColor: NODE_COLORS.tenantGroupLisa.backgroundColor,
              border: `2px dashed ${NODE_COLORS.tenantGroupLisa.borderColor}`,
              borderRadius: '4px',
            }}
          />
        </div>
        <span>Phone Tenant</span>
      </div>
    </div>
  );
};

const NestedFlow = ({ data }: { data: ApiOrgHierarchy }) => {
  const { nodes: transformedNodes, edges: transformedEdges } =
    transformToReactFlowData(data);
  const [nodes, setNodes, onNodesChange] = useNodesState(transformedNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(transformedEdges);

  const onConnect = useCallback((connection) => {
    setEdges((eds) => addEdge(connection, eds));
  }, []);

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <style>{`
        .react-flow__handle {
          visibility: hidden;
        }
      `}</style>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        className="react-flow-subflows-example"
        fitView
        nodesDraggable={false}
        nodesFocusable={false}
      >
        <MiniMap />
        <Controls />
        <Background color="#E6E6E6" />
      </ReactFlow>
      <Legend />
    </div>
  );
};

const LAYOUT_CONFIG = {
  nodeWidth: 180,
  nodeHeight: 50,
  groupPadding: 20,
  groupPaddingLisa: 25,
};

function getLayoutedElements(
  nodes: Node[],
  edges: Edge[],
  tenantToLocationIds: Map<string, Set<string>>
) {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: 'TB', ranksep: 100, nodesep: 50 });

  // Add nodes to dagre (excluding background rectangles)
  nodes.forEach((node) => {
    if (node.type !== 'backgroundRect') {
      dagreGraph.setNode(node.id, {
        width: LAYOUT_CONFIG.nodeWidth,
        height: LAYOUT_CONFIG.nodeHeight,
      });
    }
  });

  // Add edges to dagre
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  // Calculate layout
  dagre.layout(dagreGraph);

  // Update node positions from dagre
  const layoutedNodes = nodes.map((node) => {
    if (node.type === 'backgroundRect') {
      return node; // Background rectangles will be positioned after
    }

    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - LAYOUT_CONFIG.nodeWidth / 2,
        y: nodeWithPosition.y - LAYOUT_CONFIG.nodeHeight / 2,
      },
    };
  });

  // Now calculate background rectangles for each tenant group
  const backgroundRects: Node[] = [];
  tenantToLocationIds.forEach((locationIds, tenantId) => {
    const tenantNode = nodes.find((n) => n.id === `tenant-bg-${tenantId}`);
    if (!tenantNode || locationIds.size === 0) return;

    // Find all location nodes for this tenant
    const tenantLocations = layoutedNodes.filter((n) =>
      locationIds.has(n.id.replace('location-', ''))
    );

    if (tenantLocations.length === 0) return;

    const isLisa = tenantNode.data.sourceType === 'lisa';
    const padding = isLisa ? LAYOUT_CONFIG.groupPaddingLisa : LAYOUT_CONFIG.groupPadding;
    const paddingY = padding + (isLisa ? 16 : 0); // Extra vertical padding for Lisa
    const labelSpace = 30;

    // Calculate bounding box
    const minX = Math.min(...tenantLocations.map((n) => n.position.x));
    const maxX = Math.max(
      ...tenantLocations.map((n) => n.position.x + LAYOUT_CONFIG.nodeWidth)
    );
    const minY = Math.min(...tenantLocations.map((n) => n.position.y));
    const maxY = Math.max(
      ...tenantLocations.map((n) => n.position.y + LAYOUT_CONFIG.nodeHeight)
    );

    const rectX = minX - padding;
    const rectY = minY - paddingY - labelSpace;
    const rectWidth = maxX - minX + padding * 2;
    const rectHeight = maxY - minY + paddingY * 2 + labelSpace;

    backgroundRects.push({
      ...tenantNode,
      position: { x: rectX, y: rectY },
      style: {
        ...tenantNode.style,
        width: rectWidth,
        height: rectHeight,
        zIndex: -1,
      },
    });
  });

  // Combine background rectangles with other nodes
  const allNodes = [
    ...backgroundRects,
    ...layoutedNodes.filter((n) => n.type !== 'backgroundRect'),
  ];

  return { nodes: allNodes, edges };
}

function transformToReactFlowData(data: ApiOrgHierarchy) {
  const nodes: any[] = [];
  const edges: any[] = [];

  // Create org node
  nodes.push({
    id: `org-${data.orgId || 'unknown'}`,
    type: 'input',
    data: { label: data.orgName || 'Unknown Organization' },
    position: { x: 0, y: 0 }, // Will be positioned by dagre
    style: {
      backgroundColor: NODE_COLORS.organization.backgroundColor,
      color: 'white',
      border: `2px solid ${NODE_COLORS.organization.borderColor}`,
      padding: '10px 20px',
      borderRadius: '8px',
      fontWeight: 'bold',
      width: LAYOUT_CONFIG.nodeWidth,
    },
  });

  // Map tenants to their location IDs
  const tenantToLocationIds = new Map<string, Set<string>>();
  const tenantInfo = new Map<string, { tenantName: string; source: string }>();

  data.divisions?.forEach((division) => {
    division.locations?.forEach((location) => {
      location.phoneTenants?.forEach((tenant) => {
        const key = tenant.tenantId;
        if (!key) return;
        if (!tenantToLocationIds.has(key)) {
          tenantToLocationIds.set(key, new Set<string>());
          tenantInfo.set(key, {
            tenantName: tenant.tenantName || 'Unknown Tenant',
            source: tenant.source || '',
          });
        }
        tenantToLocationIds.get(key)!.add(location.locationId || '');
      });
    });
  });

  // Create background rectangle nodes for each tenant
  tenantToLocationIds.forEach((locationIds, tenantId) => {
    const info = tenantInfo.get(tenantId);
    if (!info) return;

    const isLisa = info.source === 'Phone Tenant Id in Lisa';
    const nodeColor = isLisa ? NODE_COLORS.tenantGroupLisa : NODE_COLORS.tenantGroupPDS;

    nodes.push({
      id: `tenant-bg-${tenantId}`,
      type: 'backgroundRect',
      data: {
        label: info.tenantName,
        tenantId: tenantId,
        backgroundColor: nodeColor.backgroundColor,
        border: `2px dashed ${nodeColor.borderColor}`,
        labelColor: nodeColor.labelColor,
        fontSize: 14,
        sourceType: isLisa ? 'lisa' : 'pds',
      },
      position: { x: 0, y: 0 }, // Will be calculated after layout
      zIndex: -1,
      selectable: true,
      draggable: false,
      focusable: true,
    });
  });

  data.divisions?.forEach((division) => {
    const divisionNodeId = `division-${division.divisionId || 'unknown'}`;

    // Create division node
    nodes.push({
      id: divisionNodeId,
      data: { label: division.divisionName || 'Unknown Division' },
      position: { x: 0, y: 0 }, // Will be positioned by dagre
      style: {
        backgroundColor: NODE_COLORS.division.backgroundColor,
        color: 'white',
        border: `2px solid ${NODE_COLORS.division.borderColor}`,
        padding: '8px 16px',
        borderRadius: '6px',
        width: LAYOUT_CONFIG.nodeWidth,
      },
    });

    // Create edge from org to division
    edges.push({
      id: `e-org-${divisionNodeId}`,
      source: `org-${data.orgId || 'unknown'}`,
      target: divisionNodeId,
      type: 'step',
    });

    division.locations?.forEach((location) => {
      const locationNodeId = `location-${location.locationId || 'unknown'}`;

      // Create location node (no parent relationship)
      nodes.push({
        id: locationNodeId,
        data: { label: location.locationName || 'Unknown Location' },
        position: { x: 0, y: 0 }, // Will be positioned by dagre
        style: {
          backgroundColor: NODE_COLORS.location.backgroundColor,
          color: 'white',
          border: `2px solid ${NODE_COLORS.location.borderColor}`,
          padding: '6px 12px',
          borderRadius: '4px',
          width: LAYOUT_CONFIG.nodeWidth,
        },
      });

      // Create edge from division to location
      edges.push({
        id: `e-${divisionNodeId}-${locationNodeId}`,
        source: divisionNodeId,
        target: locationNodeId,
        type: 'step',
      });
    });
  });

  // Use dagre to layout the graph
  return getLayoutedElements(nodes, edges, tenantToLocationIds);
}
